import { Borough, getBoroughBySlug } from './boroughs';

/** Route slugs must match `/resin-driveways-{slug}/` in App.tsx */
export const SERVICE_AREA_ROUTE_SLUGS = [
  'westminster',
  'camden',
  'islington',
  'kensington',
  'hackney',
  'tower-hamlets',
  'greenwich',
  'southwark',
  'lambeth',
  'wandsworth',
  'hammersmith',
  'chelsea',
  'fulham',
  'richmond',
  'kingston',
  'merton',
  'sutton',
  'croydon',
  'bromley',
  'bexley',
  'havering',
  'redbridge',
  'waltham-forest',
  'enfield',
  'barnet',
  'haringey',
  'newham',
  'barking',
  'hounslow',
  'ealing',
  'brent',
  'harrow',
  'hillingdon',
] as const;

export type ServiceAreaRouteSlug = (typeof SERVICE_AREA_ROUTE_SLUGS)[number];

/** Maps split marketing URLs to combined borough records in boroughs.ts */
const ROUTE_TO_DATA_SLUG: Partial<Record<ServiceAreaRouteSlug, string>> = {
  kensington: 'kensington-chelsea',
  chelsea: 'kensington-chelsea',
  hammersmith: 'hammersmith-fulham',
  fulham: 'hammersmith-fulham',
  richmond: 'richmond-upon-thames',
  kingston: 'kingston-upon-thames',
  barking: 'barking-dagenham',
};

const DISPLAY_NAME_OVERRIDES: Partial<Record<ServiceAreaRouteSlug, string>> = {
  kensington: 'Kensington',
  chelsea: 'Chelsea',
  hammersmith: 'Hammersmith',
  fulham: 'Fulham',
  richmond: 'Richmond upon Thames',
  kingston: 'Kingston upon Thames',
  barking: 'Barking & Dagenham',
};

export function isValidServiceAreaRouteSlug(s: string): s is ServiceAreaRouteSlug {
  return (SERVICE_AREA_ROUTE_SLUGS as readonly string[]).includes(s);
}

export interface LondonGuideContext {
  routeSlug: ServiceAreaRouteSlug;
  displayName: string;
  borough: Borough;
  servicePagePath: string;
  blogPath: string;
  canonicalUrl: string;
}

const SITE = 'https://www.resinsurfaceslondon.co.uk';

export function getLondonGuideContext(routeSlug: string): LondonGuideContext | null {
  if (!isValidServiceAreaRouteSlug(routeSlug)) return null;
  const dataSlug = ROUTE_TO_DATA_SLUG[routeSlug] ?? routeSlug;
  const borough = getBoroughBySlug(dataSlug);
  if (!borough) return null;
  const displayName = DISPLAY_NAME_OVERRIDES[routeSlug] ?? borough.name;
  return {
    routeSlug,
    displayName,
    borough,
    servicePagePath: `/resin-driveways-${routeSlug}/`,
    blogPath: `/blog/london/${routeSlug}/`,
    canonicalUrl: `${SITE}/blog/london/${routeSlug}/`,
  };
}

/** Neighbouring guides in the list (wraps) — for internal linking / SEO */
export function getRelatedLondonGuides(
  current: ServiceAreaRouteSlug,
  max = 4
): { slug: ServiceAreaRouteSlug; displayName: string; blogPath: string }[] {
  const ix = SERVICE_AREA_ROUTE_SLUGS.indexOf(current);
  const out: { slug: ServiceAreaRouteSlug; displayName: string; blogPath: string }[] = [];
  const seen = new Set<string>([current]);
  for (let delta = 1; out.length < max && delta < SERVICE_AREA_ROUTE_SLUGS.length; delta++) {
    const next = SERVICE_AREA_ROUTE_SLUGS[(ix + delta) % SERVICE_AREA_ROUTE_SLUGS.length];
    const prev =
      SERVICE_AREA_ROUTE_SLUGS[(ix - delta + SERVICE_AREA_ROUTE_SLUGS.length) % SERVICE_AREA_ROUTE_SLUGS.length];
    for (const s of [next, prev]) {
      if (!seen.has(s)) {
        seen.add(s);
        const ctx = getLondonGuideContext(s);
        if (ctx) {
          out.push({ slug: s, displayName: ctx.displayName, blogPath: ctx.blogPath });
        }
        if (out.length >= max) break;
      }
    }
  }
  return out;
}

export function getAllLondonGuideArticleSummaries(): {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
}[] {
  const images = [
    '/images/resin-surfaces-bound-work1.jpeg',
    '/images/resin-surfaces-bound-work2.jpeg',
    '/images/resin-surfaces-bound-work3.jpeg',
    '/images/resin-surfaces-bound-work4.jpeg',
    '/images/resin-surfaces-bound-work5.jpeg',
    '/images/resin-surfaces-bound-work6.jpeg',
    '/images/resin-surfaces-bound-work7.jpeg',
    '/images/resin-surfaces-bound-work8.jpeg',
    '/images/resin-surfaces-bound-work9.jpeg',
    '/images/resin-surfaces-bound-work10.jpeg',
  ];
  return SERVICE_AREA_ROUTE_SLUGS.map((slug, i) => {
    const ctx = getLondonGuideContext(slug)!;
    const previewAreas = ctx.borough.keyAreas.slice(0, 3).join(', ');
    return {
      id: `london/${slug}`,
      title: `Resin Bound Driveways in ${ctx.displayName}, London: Local Guide`,
      excerpt: `Permeable resin driveways and pathways in ${ctx.displayName}: SUDS compliance, typical costs, planning considerations, and design tips for ${previewAreas} and nearby.`,
      category: 'London',
      date: '2025-03-26',
      readTime: '14 min read',
      image: images[i % images.length],
    };
  });
}
